import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/api";
import { fmtDate } from "@/lib/format";
import { money } from "@/lib/format";
import { ShieldCheck, Lock, Unlock, ChevronLeft, ChevronRight, Users, KeyRound, FileText, Receipt, Recycle, Sparkles, RefreshCw, Mail } from "lucide-react";

const KEY = "docket_admin";

export default function Admin() {
  const navigate = useNavigate();
  const [token, setToken] = useState(localStorage.getItem(KEY) || "");
  const [pass, setPass] = useState("");
  const [loading, setLoading] = useState(false);
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [codes, setCodes] = useState([]);
  const [tab, setTab] = useState("users");
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const adminApi = useCallback(() => axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } }), [token]);

  const load = useCallback(async () => {
    try {
      const a = adminApi();
      const [o, u, c] = await Promise.all([a.get("/admin/overview"), a.get("/admin/users"), a.get("/admin/codes")]);
      setOverview(o.data); setUsers(u.data.users); setCodes(c.data.codes);
    } catch (e) {
      if (e?.response?.status === 401) { localStorage.removeItem(KEY); setToken(""); }
    }
  }, [adminApi]);

  useEffect(() => { if (token) load(); }, [token, load]);

  const login = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password: pass });
      localStorage.setItem(KEY, data.token); setToken(data.token);
    } catch (e) { toast.error(e?.response?.data?.detail || "Incorrect passcode"); }
    finally { setLoading(false); }
  };

  const toggleLock = async (u) => {
    try { await adminApi().post(`/admin/users/${u.user_id}/${u.locked ? "unlock" : "lock"}`); toast.success(u.locked ? "Account unlocked" : "Account locked"); load(); }
    catch { toast.error("Action failed"); }
  };

  const openUser = async (u) => {
    setDetailLoading(true); setDetail({ loading: true, user_id: u.user_id });
    try { const { data } = await adminApi().get(`/admin/users/${u.user_id}`); setDetail(data); }
    catch { toast.error("Couldn't load user"); setDetail(null); }
    finally { setDetailLoading(false); }
  };

  const generateCodes = async () => {
    const n = parseInt(window.prompt("How many new codes?", "1") || "0", 10);
    if (!n || n < 1) return;
    try { const { data } = await adminApi().post("/admin/codes/generate", { count: n }); toast.success(`Created: ${data.created.join(", ")}`); load(); }
    catch { toast.error("Couldn't generate codes"); }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-onSurface flex justify-center">
        <div className="w-full max-w-md min-h-screen flex flex-col px-6 pt-16 pb-8 text-onBrand">
          <button data-testid="admin-exit" onClick={() => navigate("/home")} className="flex items-center gap-1 text-white/60 text-sm mb-8"><ChevronLeft size={18} /> Back to app</button>
          <div className="w-16 h-16 rounded-[20px] bg-brandPrimary flex items-center justify-center mb-6"><ShieldCheck size={28} /></div>
          <h1 className="font-heading text-3xl font-extrabold">Admin access</h1>
          <p className="text-white/60 mt-2 text-sm">Enter the admin passcode to manage accounts and usage.</p>
          <input
            data-testid="admin-pass"
            type="password"
            value={pass}
            onChange={(e) => setPass(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") login(); }}
            placeholder="Passcode"
            className="mt-8 w-full bg-white/10 border border-white/20 rounded-[14px] px-4 py-4 text-lg text-onBrand placeholder:text-white/40 focus:outline-none focus:border-brandPrimary"
          />
          <button data-testid="admin-login-btn" disabled={loading || !pass} onClick={login} className="mt-4 w-full bg-brandPrimary text-onBrand rounded-full h-14 font-heading font-bold disabled:opacity-40 active:scale-[0.98] transition-transform">{loading ? "Checking…" : "Enter"}</button>
        </div>
      </div>
    );
  }

  const stat = [
    { label: "Users", value: overview?.users, icon: Users },
    { label: "Unlocked", value: overview?.unlocked, icon: Unlock },
    { label: "Documents", value: overview?.documents, icon: FileText },
    { label: "Kai msgs", value: overview?.kai_messages, icon: Sparkles },
    { label: "Codes free", value: overview?.codes_free, icon: KeyRound },
    { label: "Locked", value: overview?.locked, icon: Lock },
  ];

  return (
    <div className="min-h-screen bg-surfaceSecondary flex justify-center">
      <div className="w-full max-w-md min-h-screen bg-surfaceSecondary pb-10">
        <header className="sticky top-0 z-40 bg-onSurface text-onBrand px-4 h-16 flex items-center gap-3">
          <button data-testid="admin-back" onClick={() => navigate("/home")} className="w-9 h-9 -ml-1 rounded-full flex items-center justify-center hover:bg-white/10"><ChevronLeft size={22} /></button>
          <div className="flex-1"><h1 className="font-heading text-lg font-bold">Docket Admin</h1></div>
          <button data-testid="admin-refresh" onClick={load} className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-white/10"><RefreshCw size={18} /></button>
          <button data-testid="admin-logout" onClick={() => { localStorage.removeItem(KEY); setToken(""); }} className="text-xs text-white/60 ml-1">Exit</button>
        </header>

        <div className="p-4">
          <div className="grid grid-cols-3 gap-2.5">
            {stat.map((s) => (
              <div key={s.label} className="bg-surface rounded-[14px] border border-border p-3">
                <s.icon size={16} className="text-brandPrimary" />
                <p className="font-heading text-xl font-extrabold text-onSurface mt-1.5 tabular-nums">{s.value ?? "–"}</p>
                <p className="text-[11px] text-muted-foreground">{s.label}</p>
              </div>
            ))}
          </div>

          <div className="flex gap-2 mt-5">
            {["users", "codes"].map((t) => (
              <button key={t} data-testid={`admin-tab-${t}`} onClick={() => setTab(t)} className={`px-4 py-2 rounded-full text-sm font-semibold capitalize transition-colors ${tab === t ? "bg-brandPrimary text-onBrand" : "bg-surface border border-border text-onSurface"}`}>{t}</button>
            ))}
          </div>

          {tab === "users" ? (
            detail ? (
              <UserDetail detail={detail} loading={detailLoading} onBack={() => setDetail(null)} onToggleLock={toggleLock} />
            ) : (
              <div className="mt-3 space-y-2">
              {users.map((u) => (
                <div key={u.user_id} data-testid={`admin-user-${u.user_id}`} onClick={() => openUser(u)} className="bg-surface rounded-[12px] border border-border p-3.5 cursor-pointer hover:border-brandPrimary transition-colors">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-heading font-bold text-sm text-onSurface truncate">{u.business_name || u.name || u.email}</p>
                      <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                    </div>
                    <button data-testid={`admin-lock-${u.user_id}`} onClick={(e) => { e.stopPropagation(); toggleLock(u); }} className={`shrink-0 text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1 ${u.locked ? "bg-error/10 text-error" : "bg-surfaceTertiary text-onSurface"}`}>
                      {u.locked ? <><Unlock size={13} /> Unlock</> : <><Lock size={13} /> Lock</>}
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    <Tag on={u.unlocked} label={u.unlocked ? "Premium" : "Free"} />
                    {u.subscription_status && <Tag on label={u.subscription_status} />}
                    {!u.onboarded && <Tag label="Not onboarded" />}
                    {u.locked && <Tag danger label="Locked" />}
                  </div>
                  <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                    <span>{u.documents} docs</span><span>{u.kai_messages} Kai</span><span>joined {fmtDate(u.created_at)}</span>
                    <ChevronRight size={15} className="ml-auto text-brandPrimary" />
                  </div>
                </div>
              ))}
              </div>
            )
          ) : (
            <div className="mt-3 space-y-2">
              <button data-testid="admin-gen-codes" onClick={generateCodes} className="w-full bg-brandPrimary text-onBrand rounded-full h-11 font-heading font-bold text-sm flex items-center justify-center gap-2 active:scale-[0.98] transition-transform">
                <KeyRound size={16} /> Generate new code(s)
              </button>
              {codes.map((c) => (
                <div key={c.code} className="bg-surface rounded-[12px] border border-border p-3.5 flex items-center justify-between">
                  <span className="font-heading text-2xl font-extrabold tracking-[0.3em] text-onSurface">{c.code}</span>
                  <div className="text-right">
                    {c.used ? <><span className="text-xs font-bold text-error">USED</span><p className="text-[11px] text-muted-foreground">{c.used_by_email || ""}</p></> : <span className="text-xs font-bold text-success">Available</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Tag = ({ label, on, danger }) => (
  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${danger ? "bg-error/10 text-error" : on ? "bg-success/10 text-success" : "bg-surfaceTertiary text-muted-foreground"}`}>{label}</span>
);

const DOC_ICON = { quote: FileText, invoice: Receipt, waste_note: Recycle };

const UserDetail = ({ detail, loading, onBack, onToggleLock }) => {
  if (loading || detail?.loading) return <div className="mt-3 h-64 bg-surfaceTertiary rounded-[14px] animate-pulse" />;
  const { user, access, stats, recent_documents } = detail;
  const metrics = [
    { label: "Quotes", value: stats.by_type.quote || 0, icon: FileText },
    { label: "Invoices", value: stats.by_type.invoice || 0, icon: Receipt },
    { label: "Waste notes", value: stats.by_type.waste_note || 0, icon: Recycle },
    { label: "Clients", value: stats.clients, icon: Users },
    { label: "Kai msgs", value: stats.kai_messages, icon: Sparkles },
    { label: "Total docs", value: stats.documents, icon: FileText },
  ];
  return (
    <div className="mt-3">
      <button data-testid="admin-user-back" onClick={onBack} className="flex items-center gap-1 text-sm font-semibold text-muted-foreground mb-3"><ChevronLeft size={16} /> All users</button>

      <div className="bg-surface rounded-[14px] border border-border p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="font-heading font-extrabold text-lg text-onSurface truncate">{user.business_name || user.name || user.email}</p>
            <p className="text-xs text-muted-foreground flex items-center gap-1 truncate"><Mail size={11} /> {user.email}</p>
            {user.phone && <p className="text-xs text-muted-foreground mt-0.5">{user.phone}</p>}
          </div>
          <button data-testid="admin-detail-lock" onClick={() => onToggleLock(user)} className={`shrink-0 text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1 ${user.locked ? "bg-error/10 text-error" : "bg-surfaceTertiary text-onSurface"}`}>
            {user.locked ? <><Unlock size={13} /> Unlock</> : <><Lock size={13} /> Lock</>}
          </button>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-3">
          <Tag on label={user.trade_label || "No trade"} />
          <Tag on={access.premium} label={access.premium ? "Premium" : access.trial_active ? `Trial · ${access.trial_days_left}d left` : "Free/expired"} />
          {access.subscription_status && <Tag on label={access.subscription_status} />}
          {access.cancel_at_period_end && <Tag danger label="Cancelling" />}
          <Tag on label={user.auth_provider === "email" ? (user.email_verified ? "Email ✓" : "Email unverified") : "Google"} />
          {user.vat_registered && <Tag on label={`VAT ${user.vat_number || "reg"}`} />}
          {user.locked && <Tag danger label="Locked" />}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2.5 mt-3">
        {metrics.map((m) => (
          <div key={m.label} className="bg-surface rounded-[12px] border border-border p-3">
            <m.icon size={15} className="text-brandPrimary" />
            <p className="font-heading text-xl font-extrabold text-onSurface mt-1 tabular-nums">{m.value}</p>
            <p className="text-[11px] text-muted-foreground">{m.label}</p>
          </div>
        ))}
      </div>

      <div className="bg-onSurface text-onBrand rounded-[14px] p-4 mt-3 grid grid-cols-3 gap-2">
        <div><p className="text-[10px] uppercase tracking-wider text-white/60">Invoiced</p><p className="font-heading text-lg font-extrabold">{money(stats.invoiced_total)}</p></div>
        <div><p className="text-[10px] uppercase tracking-wider text-white/60">Paid</p><p className="font-heading text-lg font-extrabold text-success">{money(stats.paid_total)}</p></div>
        <div><p className="text-[10px] uppercase tracking-wider text-white/60">Outstanding</p><p className="font-heading text-lg font-extrabold text-brandPrimary">{money(stats.outstanding_total)}</p></div>
      </div>

      <p className="text-xs text-muted-foreground mt-3">Joined {fmtDate(user.created_at)}{stats.last_document_at ? ` · last doc ${fmtDate(stats.last_document_at)}` : ""}</p>

      <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mt-5 mb-2">Recent documents</p>
      <div className="space-y-2 pb-6">
        {recent_documents.length === 0 ? <p className="text-sm text-muted-foreground">No documents yet.</p> : recent_documents.map((d) => {
          const Icon = DOC_ICON[d.type] || FileText;
          return (
            <div key={d.id} className="bg-surface rounded-[12px] border border-border p-3 flex items-center gap-3">
              <Icon size={16} className="text-brandPrimary shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-heading font-bold text-sm text-onSurface truncate">{d.number} · {d.client || "No client"}</p>
                <p className="text-xs text-muted-foreground truncate">{d.title || d.type} · {fmtDate(d.created_at)}</p>
              </div>
              {d.total != null && d.type !== "waste_note" && <span className="text-sm font-bold text-onSurface">{money(d.total)}</span>}
              {d.status && <Tag on={d.status === "paid"} label={d.status} />}
            </div>
          );
        })}
      </div>
    </div>
  );
};
